/**
 * API service for Chat backend
 */
import axios from 'axios';
import config from '../config';

const API_BASE_URL = config.apiUrl;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect ONLY if not on login page
      const isLoginPage = window.location.pathname === '/login';
      
      if (!isLoginPage) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// Authentication API
// ============================================

export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// ============================================
// Chat Session API
// ============================================

export const sessionAPI = {
  list: async (limit = 50) => {
    const response = await api.get('/sessions', { params: { limit } });
    return response.data;
  },

  create: async (title = null) => {
    const response = await api.post('/sessions', { title });
    return response.data;
  },

  get: async (sessionId) => {
    const response = await api.get(`/sessions/${sessionId}`);
    return response.data;
  },

  delete: async (sessionId) => {
    const response = await api.delete(`/sessions/${sessionId}`);
    return response.data;
  },
};

// ============================================
// Message API
// ============================================

export const messageAPI = {
  getMessages: async (sessionId, limit = 100) => {
    const response = await api.get(`/sessions/${sessionId}/messages`, {
      params: { limit },
    });
    return response.data;
  },
};

// ============================================
// Chat Streaming (SSE)
// ============================================

export class ChatStream {
  constructor(onEvent, onError) {
    this.onEvent = onEvent;
    this.onError = onError;
    this.eventSource = null;
  }

  async sendMessage(message, sessionId = null) {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    // Get user info for department code
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const departmentCode = user?.department || '';  // Use department code (HR, SALES)

    // Close any existing connection
    this.close();

    // Create request body
    const body = {
      message,
      session_id: sessionId,
    };

    // Use fetch for SSE streaming
    try {
      const response = await fetch(`${API_BASE_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Dept-ID': departmentCode,  // Send department code (HR/SALES)
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Read stream
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              this.onEvent(data);
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Stream error:', error);
      if (this.onError) {
        this.onError(error);
      }
    }
  }

  close() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}

export default api;

